// js/common/base.js
import Lenis from "@studio-freight/lenis";
import { gsap, ScrollTrigger } from "../common/gsap";

/**
 * =========================================================
 * Lenis / ScrollTrigger base control
 * - 今回の要件：prefers-reduced-motion がONでも Lenis を止めない（完全統一）
 * =========================================================
 */

/* =========================
   Global switches
========================= */
// ★ true なら “動きの軽減” を無視して常に動かす（今回の方針）
const FORCE_MOTION = true;

// PCのみLenis（これまで通り）
const mqPC = window.matchMedia("(min-width: 769px)");

// ★ reduceMotionはスイッチ経由で決める
const mqlReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
const reduceMotion = () => (!FORCE_MOTION && mqlReduce.matches);

let lenis = null;

/* =========================
   Lenis 制御（GSAP ticker駆動）
========================= */
let tickerFn = null;
let stListenersInitialized = false;

function startTicker() {
  if (tickerFn) return;
  tickerFn = () => {
    lenis?.raf(performance.now());
  };
  gsap.ticker.add(tickerFn);
}

function stopTicker() {
  if (!tickerFn) return;
  gsap.ticker.remove(tickerFn);
  tickerFn = null;
}

/**
 * ScrollTrigger 側のイベントを一度だけ登録
 */
function initScrollTriggerListeners() {
  if (stListenersInitialized) return;
  stListenersInitialized = true;

  ScrollTrigger.addEventListener("refreshInit", () => {
    try { lenis?.stop?.(); } catch (_) {}
    stopTicker();
  });

  ScrollTrigger.addEventListener("refresh", () => {
    try { lenis?.start?.(); } catch (_) {}
    startTicker();
  });
}

function initLenis() {

  if (reduceMotion() || !mqPC.matches || lenis) return;

  document.documentElement.style.scrollBehavior = "auto";

  lenis = new Lenis({
    lerp: 0.14,
    smoothWheel: true,
    smoothTouch: false,
    wheelMultiplier: 1.4,
  });

  lenis.on("scroll", ScrollTrigger.update);

  initScrollTriggerListeners();
  startTicker();

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }
}

function destroyLenis() {
  if (!lenis) return;
  try { lenis.stop?.(); } catch (_) {}
  stopTicker();
  lenis.destroy();
  lenis = null;
}

/* =========================
   ビューポート切替に追随（matchMedia フォールバック込み）
========================= */
const handleMqPCChange = (e) => {
  if (reduceMotion()) return;

  if (e.matches) {
    initLenis();
    requestAnimationFrame(() => {
      setTimeout(() => ScrollTrigger.refresh(), 50);
    });
  } else {
    destroyLenis();
    requestAnimationFrame(() => ScrollTrigger.refresh());
  }
};

if (mqPC.addEventListener) {
  mqPC.addEventListener("change", handleMqPCChange);
} else if (mqPC.addListener) {
  mqPC.addListener(handleMqPCChange);
}

/* =========================
   もしOS側で軽減設定が切り替わった時に追随したい場合（任意）
   FORCE_MOTION=trueなら実質何も起きませんが、false運用に戻す時に便利
========================= */
const handleReduceChange = () => {
  // FORCE_MOTION=trueなら reduceMotion() は常に false なので、何も変わらない
  if (reduceMotion()) {
    destroyLenis();
    ScrollTrigger.refresh();
  } else {
    if (!lenis && mqPC.matches) initLenis();
    ScrollTrigger.refresh();
  }
};

if (mqlReduce.addEventListener) {
  mqlReduce.addEventListener("change", handleReduceChange);
} else if (mqlReduce.addListener) {
  mqlReduce.addListener(handleReduceChange);
}

/* =========================
   bfcache 復帰対策（iOS/Safari）
========================= */
window.addEventListener("pageshow", (e) => {
  if (e.persisted) {
    if (!lenis && !reduceMotion() && mqPC.matches) initLenis();
    requestAnimationFrame(() => {
      setTimeout(() => ScrollTrigger.refresh(), 50);
    });
  }
});

/* =========================
   スクロール制御ユーティリティ
========================= */
export function scrollToTarget(target, options = {}) {
  const {
    offset = 0,
    duration = 1.0,
    ...rest
  } = options;

  if (lenis) {
    lenis.scrollTo(target, { offset: -offset, duration, ...rest });
  } else {
    const el = typeof target === "string" ? document.querySelector(target) : target;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
}

export const getLenis = () => lenis;

// ★ 必要なら外部から強制制御できるように公開（デバッグ/特定ページでOFF等）
export const enableLenis = () => initLenis();
export const disableLenis = () => destroyLenis();

/* =========================
   ページ内リンク（同一ページ内のみ）
========================= */
function initAnchorScroll() {
  const header = document.querySelector(".l-header");
  const headerH = () => (header ? header.offsetHeight : 0);

  document.querySelectorAll('a[href*="#"]').forEach((link) => {
    link.addEventListener(
      "click",
      (e) => {
        const href = link.getAttribute("href");
        if (!href || href === "#") return;

        let url;
        try {
          url = new URL(href, location.href);
        } catch (_) {
          return;
        }
        if (url.pathname !== location.pathname) return;

        const target = document.querySelector(url.hash);
        if (!target) return;

        e.preventDefault();
        scrollToTarget(target, { offset: headerH() });
      },
      { passive: false }
    );
  });

  if (location.hash) {
    const t = document.querySelector(location.hash);
    if (t) {
      requestAnimationFrame(() => {
        scrollToTarget(t, { offset: headerH(), duration: 0.8 });
      });
    }
  }
}

/* =========================
   起動
========================= */
window.addEventListener("DOMContentLoaded", () => {
  if (!reduceMotion() && mqPC.matches) initLenis();
  initAnchorScroll();

  let refreshTimer;
  const scheduleRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 120);
  };

  window.addEventListener(
    "load",
    () => {
      scheduleRefresh();
    },
    { once: true }
  );

  window.addEventListener("resize", scheduleRefresh);
});
